import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Avatar,
  useTheme,
  alpha,
  Theme,
} from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PaletteIcon from "@mui/icons-material/Palette";

// Definindo explicitamente o tipo para 'color'
interface Reason {
  icon: React.ReactElement;
  title: string;
  description: string;
  color: "primary" | "secondary" | "error" | "info" | "success" | "warning";
}

const reasons: Reason[] = [
  {
    icon: <SpeedIcon sx={{ fontSize: 30 }} />,
    title: "Performance Otimizada",
    description:
      "Soluções rápidas e eficientes que garantem a melhor experiência.",
    color: "primary",
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 30 }} />,
    title: "Segurança Confiável",
    description: "Protegemos seus dados com as melhores práticas de segurança.",
    color: "secondary",
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 30 }} />,
    title: "Suporte Dedicado",
    description:
      "Nossa equipe está pronta para ajudar você a qualquer momento.",
    color: "error",
  },
  {
    icon: <PaletteIcon sx={{ fontSize: 30 }} />,
    title: "Design Moderno",
    description:
      "Interfaces intuitivas e visualmente atraentes para seus usuários.",
    color: "info",
  },
];

// Função auxiliar para obter a cor da paleta com segurança
const getPaletteColorSafe = (
  theme: Theme,
  colorName: Reason["color"]
): string => {
  return theme.palette[colorName]?.main || theme.palette.primary.main;
};

const getPaletteDarkColorSafe = (
  theme: Theme,
  colorName: Reason["color"]
): string => {
  return theme.palette[colorName]?.dark || theme.palette.primary.dark;
};

export const WhyChooseUsSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: alpha(theme.palette.primary.light, 0.1),
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="overline"
          align="center"
          display="block"
          gutterBottom
          sx={{ color: theme.palette.primary.main }}
        >
          Diferenciais
        </Typography>
        <Typography
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            mb: { xs: 6, md: 8 },
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          Por que escolher nossa solução?
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {reasons.map((reason, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: "center",
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.shape.borderRadius,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  backgroundColor: theme.palette.background.paper,
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: `0px 8px 20px ${alpha(
                      getPaletteColorSafe(theme, reason.color),
                      0.1
                    )}`,
                    borderColor: alpha(
                      getPaletteColorSafe(theme, reason.color),
                      0.3
                    ),
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(
                      getPaletteColorSafe(theme, reason.color),
                      0.15
                    ),
                    color: getPaletteDarkColorSafe(theme, reason.color),
                    width: 60,
                    height: 60,
                    margin: "0 auto 16px auto",
                  }}
                >
                  {reason.icon}
                </Avatar>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {reason.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reason.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
